import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  ServerIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

interface LabtechLog {
  id: string;
  syncRunId: string;
  timestamp: string;
  logType: 'CUSTOMER_SKIP' | 'RESOURCE_SKIP' | 'ERROR' | 'INFO';
  labtechAlertId: string | null;
  customerName: string | null;
  resourceName: string | null;
  reason: string;
  suggestedAction: string | null;
  createdAt: string;
  syncRun?: {
    id: string;
    startedAt: string;
    status: string;
    alertsSkipped: number;
    alertsCreated: number;
  };
}

interface Stats {
  totalLogs: number;
  customerSkips: number;
  resourceSkips: number;
  errors: number;
  lastSyncRun: {
    id: string;
    startedAt: string;
    status: string;
    alertsCreated: number;
    alertsSkipped: number;
  } | null;
  last7DaysStats: Record<string, number>;
}

const logTypeConfig = {
  CUSTOMER_SKIP: {
    label: 'Engagement Not Found',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: UserGroupIcon,
    iconColor: 'text-amber-600',
  },
  RESOURCE_SKIP: {
    label: 'Resource Not Found',
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: ServerIcon,
    iconColor: 'text-orange-600',
  },
  ERROR: {
    label: 'Processing Error',
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: ExclamationCircleIcon,
    iconColor: 'text-red-600',
  },
};

interface SyncRun {
  id: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  alertsSynced: number;
  alertsCreated: number;
  alertsResolved: number;
  alertsSkipped: number;
  _count?: { logs: number };
}

const LabtechLogList: React.FC = () => {
  const { formatDateTime } = useDateFormatter();

  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterLogType, setFilterLogType] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterSyncRunId, setFilterSyncRunId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const PAGE_SIZE_OPTIONS = [25, 50, 100];

  // Fetch sync runs for dropdown
  const { data: syncRunsData } = useQuery({
    queryKey: ['labtech-sync-runs'],
    queryFn: async () => {
      const response = await api.get('/labtech-logs/runs?limit=5');
      return response.data.data as { runs: SyncRun[]; total: number };
    },
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  // Fetch logs
  const { data, isLoading, error } = useQuery({
    queryKey: ['labtech-logs', page, pageSize, appliedSearch, filterLogType, filterCustomer, filterSyncRunId],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(appliedSearch && { search: appliedSearch }),
        ...(filterLogType && { logType: filterLogType }),
        ...(filterCustomer && { customerName: filterCustomer }),
        ...(filterSyncRunId && { syncRunId: filterSyncRunId }),
      });

      const response = await api.get(`/labtech-logs?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  // Fetch stats (filtered by sync run if selected)
  const { data: statsData } = useQuery({
    queryKey: ['labtech-logs-stats', filterSyncRunId],
    queryFn: async () => {
      const params = filterSyncRunId ? `?syncRunId=${filterSyncRunId}` : '';
      const response = await api.get(`/labtech-logs/stats${params}`);
      return response.data.data as Stats;
    },
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...(filterLogType && { logType: filterLogType }),
        ...(filterCustomer && { customerName: filterCustomer }),
      });

      const response = await api.get(`/labtech-logs/export?${params}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `labtech-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };


  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diff = now.getTime() - then.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading LabTech logs. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Header */}
      <div className="bg-white shadow rounded-lg mb-3 flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">LabTech Sync Logs</h1>
              <p className="text-sm text-gray-500 mt-1">
                Showing logs from the last 5 sync runs (auto-purged daily)
              </p>
            </div>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      {statsData && (
        <div className="bg-white shadow rounded-lg mb-3 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              <span className="flex items-center gap-1.5">
                <span className="text-gray-600">Total Logs:</span>
                <span className="font-semibold text-gray-900">{statsData.totalLogs}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span className="text-gray-600">Engagement Not Found:</span>
                <span className="font-semibold text-gray-900">{statsData.customerSkips}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                <span className="text-gray-600">Resource Not Found:</span>
                <span className="font-semibold text-gray-900">{statsData.resourceSkips}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Processing Errors:</span>
                <span className="font-semibold text-gray-900">{statsData.errors}</span>
              </span>
            </div>
            {statsData.lastSyncRun && (
              <div className="text-sm text-gray-500">
                Last sync: {formatTimeAgo(statsData.lastSyncRun.startedAt)} ({statsData.lastSyncRun.status})
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-3 px-4 py-3 flex-shrink-0">
        <form onSubmit={handleSearch} className="flex items-center gap-3">
          <select
            value={filterSyncRunId}
            onChange={(e) => {
              setFilterSyncRunId(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All Runs</option>
            {syncRunsData?.runs?.map((run: SyncRun) => (
              <option key={run.id} value={run.id}>
                {new Date(run.startedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })} - {run.status} ({run._count?.logs || 0} logs)
              </option>
            ))}
          </select>

          <select
            value={filterLogType}
            onChange={(e) => {
              setFilterLogType(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All Types</option>
            <option value="CUSTOMER_SKIP">Engagement Not Found</option>
            <option value="RESOURCE_SKIP">Resource Not Found</option>
            <option value="ERROR">Processing Error</option>
          </select>

          <input
            type="text"
            value={filterCustomer}
            onChange={(e) => setFilterCustomer(e.target.value)}
            onBlur={() => setPage(1)}
            placeholder="Filter by customer..."
            className="px-3 py-2 text-sm w-48 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />

          <div className="flex-1"></div>

          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search logs..."
              className="pl-9 pr-3 py-2 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Active Filters */}
      {(appliedSearch || filterLogType || filterCustomer || filterSyncRunId) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {filterSyncRunId && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium border border-indigo-300">
                  Run: {syncRunsData?.runs?.find((r: SyncRun) => r.id === filterSyncRunId)
                    ? new Date(syncRunsData.runs.find((r: SyncRun) => r.id === filterSyncRunId)!.startedAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : filterSyncRunId.slice(0, 8)}
                  <button
                    onClick={() => {
                      setFilterSyncRunId('');
                      setPage(1);
                    }}
                    className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {appliedSearch && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-300">
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  Search: "{appliedSearch}"
                  <button
                    onClick={() => {
                      setSearchInput('');
                      setAppliedSearch('');
                      setPage(1);
                    }}
                    className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filterLogType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium border border-purple-300">
                  Type: {logTypeConfig[filterLogType as keyof typeof logTypeConfig]?.label || filterLogType}
                  <button
                    onClick={() => {
                      setFilterLogType('');
                      setPage(1);
                    }}
                    className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filterCustomer && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm font-medium border border-pink-300">
                  Customer: {filterCustomer}
                  <button
                    onClick={() => {
                      setFilterCustomer('');
                      setPage(1);
                    }}
                    className="ml-1 hover:bg-pink-200 rounded-full p-0.5"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
            <button
              onClick={() => {
                setSearchInput('');
                setAppliedSearch('');
                setFilterLogType('');
                setFilterCustomer('');
                setFilterSyncRunId('');
                setPage(1);
              }}
              className="px-3 py-1 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-md transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="space-y-3">
          {data?.data?.logs?.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No logs found</p>
            </div>
          ) : (
            data?.data?.logs?.map((log: LabtechLog) => {
              const config = logTypeConfig[log.logType as keyof typeof logTypeConfig] || {
                label: log.logType,
                color: 'bg-gray-100 text-gray-800 border-gray-300',
                icon: ExclamationTriangleIcon,
                iconColor: 'text-gray-600',
              };
              const IconComponent = config.icon;

              return (
                <div
                  key={log.id}
                  className="bg-white shadow rounded-lg p-4 border-l-4"
                  style={{ borderLeftColor: log.logType === 'CUSTOMER_SKIP' ? '#f59e0b' : log.logType === 'RESOURCE_SKIP' ? '#f97316' : log.logType === 'ERROR' ? '#ef4444' : '#3b82f6' }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} border`}>
                            {config.label}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDateTime(log.createdAt)}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {log.customerName && (
                            <div className="text-sm">
                              <span className="text-gray-500">Customer:</span>{' '}
                              <span className="font-medium text-gray-900">{log.customerName}</span>
                            </div>
                          )}
                          {log.resourceName && (
                            <div className="text-sm">
                              <span className="text-gray-500">Resource:</span>{' '}
                              <span className="font-medium text-gray-900">{log.resourceName}</span>
                            </div>
                          )}
                          {log.labtechAlertId && (
                            <div className="text-sm">
                              <span className="text-gray-500">Alert ID:</span>{' '}
                              <span className="font-mono text-gray-700">LT-{log.labtechAlertId}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-2">
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Reason:</span> {log.reason}
                          </div>
                          {log.suggestedAction && (
                            <div className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Action:</span> {log.suggestedAction}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pagination */}
      {data?.data?.logs && data.data.logs.length > 0 && (
        <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-3 rounded-lg shadow flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data.data.total)} to{' '}
            {Math.min(page * pageSize, data.data.total)} of {data.data.total} entries
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {(() => {
              const totalPages = data.data.totalPages;
              const pages: (number | string)[] = [];

              if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}

            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= data.data.totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setPage(data.data.totalPages)}
              disabled={page >= data.data.totalPages}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabtechLogList;
